import { useEffect, useReducer } from 'react'

interface State {
  isPlaying: boolean
  currentTime: number
}

enum Actions {
  Play = 'Play',
  Pause = 'Pause',
  SetTime = 'SetTime',
}

interface PlayAction {
  type: Actions.Play
}

interface PauseAction {
  type: Actions.Pause
}

interface SetTimeAction {
  type: Actions.SetTime
  currentTime: number
}

type Action = PlayAction | PauseAction | SetTimeAction

const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case Actions.Play: {
      return {
        ...state,
        isPlaying: true,
      }
    }
    case Actions.Pause: {
      return {
        ...state,
        isPlaying: false,
      }
    }
    case Actions.SetTime: {
      return {
        ...state,
        currentTime: action.currentTime,
      }
    }
    default:
      // @ts-expect-error
      throw new Error(`${action.type} is not a valid action`)
  }
}

interface Props {
  ref: React.RefObject<HTMLAudioElement>
}

const initialState: State = {
  isPlaying: false,
  currentTime: -1,
}

export const useAudio = ({ ref }: Props) => {
  const [state, dispatch] = useReducer(reducer, initialState)

  const { isPlaying, currentTime } = state

  const el = ref.current

  const play = () => {
    if (el) {
      el.play()
      dispatch({ type: Actions.Play })
    }
  }

  const pause = () => {
    if (el) {
      el.pause()
      dispatch({ type: Actions.Pause })
    }
  }

  useEffect(() => {
    if (el) {
      el.addEventListener('ended', pause)
      return () => {
        el.removeEventListener('ended', pause)
      }
    }
  }, [el])

  useEffect(() => {
    if (el && isPlaying) {
      const interval = setInterval(() => {
        dispatch({ type: Actions.SetTime, currentTime: el.currentTime })
      }, 100)
      return () => clearInterval(interval)
    }
  }, [isPlaying, currentTime, el])

  return {
    state,
    pause,
    play,
  }
}
